import React, { useState, useEffect, useCallback } from 'react';
import { Tag } from 'antd';
import { TransitionService } from '../../services/api/transitionService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { StateService } from '../../services/api/stateService';
import { apiClient } from '../../services/apiClient';
import { UnifiedTransitionForm } from './UnifiedTransitionForm';
import { CompanyArtifactId, CArtifact } from '../../types/artifacts-branded';
import { ArtifactItemTransition } from '../../types/artifactItems';
import styles from './TransitionManager.module.css';

// Extended type for unified transition management
type UnifiedTransition = ArtifactItemTransition & {
  isPrimeTransition: boolean;
  from_state_name?: string;
  to_state_name?: string;
  default_transition_type?: string;
  demand_type?: string;
  demand_flow_type?: string;
  input_output_ratio?: number;
  is_active?: boolean;
};

interface UnifiedTransitionManagerProps {
  artifactId: CompanyArtifactId;
  usePrime: boolean;
  artifact?: CArtifact | null; // Company artifact with optional prime reference
}

export const UnifiedTransitionManager: React.FC<UnifiedTransitionManagerProps> = ({
  artifactId,
  usePrime,
  artifact,
}) => {
  const [transitions, setTransitions] = useState<UnifiedTransition[]>([]);
  const [selectedTransition, setSelectedTransition] = useState<UnifiedTransition | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [improvementFilter, setImprovementFilter] = useState<'all' | 'operational' | 'improvement'>('all');

  const loadTransitions = useCallback(async () => {
    try {
      setLoading(true);
      const allTransitions: UnifiedTransition[] = [];

      // Load states first to map state refs to state names
      let states: any[] = [];
      try {
        states = await StateService.getStatesByArtifact(artifactId);
      } catch (err) {
        console.warn('Failed to load states for transition display:', err);
      }

      // Create a map of state ID to state name for quick lookup
      const stateNameMap = new Map<string, string>();
      states.forEach((state) => {
        stateNameMap.set(state.id, state.name);
      });

      // Always load regular transitions for the artifact
      try {
        const regularTransitions = await TransitionService.getTransitionsByArtifact(artifactId);
        allTransitions.push(
          ...regularTransitions.map(
            (transition) => ({
              ...transition,
              isPrimeTransition: false,
              from_state_name: transition.input_state_ref
                ? stateNameMap.get(transition.input_state_ref) || undefined
                : undefined,
              to_state_name: transition.output_state_ref
                ? stateNameMap.get(transition.output_state_ref) || undefined
                : undefined,
            }) as UnifiedTransition
          )
        );
      } catch (err) {
        console.warn('Failed to load regular transitions:', err);
      }

      // Also load prime transitions if the artifact has a prime connection
      const primeArtifactId = artifact?.prime_artifact_id;
      if (primeArtifactId) {
        try {
          // Load prime states for mapping
          let primeStates: any[] = [];
          try {
            primeStates = await PrimeArtifactService.getPrimeArtifactStates(primeArtifactId);
            if (process.env.NODE_ENV === 'development') {
              console.log('[UnifiedTransitionManager] Loaded prime states:', {
                count: primeStates.length,
                states: primeStates.map((s) => ({ id: s.id, name: s.name })),
              });
            }
          } catch (err) {
            console.warn('Failed to load prime states for transition display:', err);
          }

          // Create a map of prime state ID to prime state name
          // Handle both string and UUID formats
          const primeStateNameMap = new Map<string, string>();
          primeStates.forEach((state) => {
            if (state.id && state.name) {
              // Store both the original ID and string version for lookup
              const stateId = String(state.id);
              primeStateNameMap.set(stateId, state.name);
              // Also store the UUID object if it's a UUID type
              if (state.id !== stateId) {
                primeStateNameMap.set(String(state.id), state.name);
              }
            }
          });

          if (process.env.NODE_ENV === 'development') {
            console.log('[UnifiedTransitionManager] Prime state name map:', {
              size: primeStateNameMap.size,
              entries: Array.from(primeStateNameMap.entries()).slice(0, 5), // Show first 5
              all_keys: Array.from(primeStateNameMap.keys()),
              all_key_types: Array.from(primeStateNameMap.keys()).map(k => typeof k),
              sample_state_id: primeStates[0]?.id,
              sample_state_id_type: typeof primeStates[0]?.id,
              sample_state_id_string: primeStates[0]?.id ? String(primeStates[0].id) : null,
            });
          }

          const primeTransitions =
            await PrimeArtifactService.getPrimeArtifactTransitions(primeArtifactId);
          
          // Only log in development mode and sample to reduce verbosity
          if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
            console.log('[UnifiedTransitionManager] Loaded prime transitions (sampled):', {
              count: primeTransitions.length
            });
          }

          allTransitions.push(
            ...primeTransitions.map(
              (transition) => {
                // The API returns from_state_id/to_state_id, not from_state_ref/to_state_ref
                // Try both field names for compatibility
                const fromStateId = (transition as any).from_state_id || transition.from_state_ref;
                const toStateId = (transition as any).to_state_id || transition.to_state_ref;
                
                // Convert state IDs to strings for consistent lookup
                const fromStateRef = fromStateId ? String(fromStateId) : null;
                const toStateRef = toStateId ? String(toStateId) : null;
                
                const fromStateName = fromStateRef
                  ? primeStateNameMap.get(fromStateRef) || undefined
                  : undefined;
                const toStateName = toStateRef
                  ? primeStateNameMap.get(toStateRef) || undefined
                  : undefined;

                if (process.env.NODE_ENV === 'development') {
                  if (!fromStateName && fromStateRef) {
                    console.warn('[UnifiedTransitionManager] Could not find prime state name for FROM state:', {
                      transition_id: transition.id,
                      transition_name: transition.name,
                      from_state_id: fromStateId,
                      from_state_ref_string: fromStateRef,
                      available_state_ids: Array.from(primeStateNameMap.keys()),
                      state_id_matches: Array.from(primeStateNameMap.keys()).map(id => ({
                        id,
                        matches: id === fromStateRef,
                        string_matches: String(id) === fromStateRef,
                      })),
                      sample_state_id_from_map: Array.from(primeStateNameMap.keys())[0],
                      sample_state_id_from_transition: fromStateRef,
                    });
                  }
                  
                  if (!toStateName && toStateRef) {
                    console.warn('[UnifiedTransitionManager] Could not find prime state name for TO state:', {
                      transition_id: transition.id,
                      transition_name: transition.name,
                      to_state_id: toStateId,
                      to_state_ref_string: toStateRef,
                      available_state_ids: Array.from(primeStateNameMap.keys()),
                      state_id_matches: Array.from(primeStateNameMap.keys()).map(id => ({
                        id,
                        matches: id === toStateRef,
                        string_matches: String(id) === toStateRef,
                      })),
                    });
                  }
                  
                  // Only log in development mode and sample to reduce verbosity
                  if ((fromStateName || toStateName) && process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
                    console.log('[UnifiedTransitionManager] Successfully resolved state names (sampled):', {
                      transition_name: transition.name,
                      from_state_name: fromStateName,
                      to_state_name: toStateName,
                    });
                  }
                }

                return {
                  id: transition.id,
                  name: transition.name,
                  description: transition.description || undefined,
                  artifact_ref: transition.prime_artifact_ref,
                  // Use from_state_id/to_state_id if available, fallback to from_state_ref/to_state_ref
                  input_state_ref: fromStateId || transition.from_state_ref || null,
                  output_state_ref: toStateId || transition.to_state_ref || null,
                  transition_type: transition.default_transition_type,
                  transition_parameters: transition.transition_parameters,
                  validation_rules: transition.validation_rules,
                  prime_artifact_state_transition_ref: transition.id,
                  demand_calculation_rules: {},
                  improvement_impact: {},
                  improvement_ref: undefined,
                  artifact_process_ref: transition.prime_process_ref,
                  input_output_ratio: 1,
                  effective_start_date: new Date().toISOString(),
                  effective_end_date: null,
                  is_active: true,
                  is_improvement: transition.is_improvement ?? false,
                  created_at: transition.created_at,
                  updated_at: transition.updated_at || new Date().toISOString(),
                  isPrimeTransition: true,
                  from_state_name: fromStateName,
                  to_state_name: toStateName,
                } as unknown as UnifiedTransition;
              }
            )
          );
        } catch (err) {
          console.warn('Failed to load prime transitions:', err);
        }
      }

      setTransitions(allTransitions);
      
      // Log for debugging - only sample in development to reduce verbosity
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('[UnifiedTransitionManager] Loaded transitions (sampled):', {
          count: allTransitions.length
        });
      }
    } catch (err) {
      setError('Failed to load transitions');
      console.error('Error loading transitions:', err);
    } finally {
      setLoading(false);
    }
  }, [artifactId, artifact]);

  useEffect(() => {
    void loadTransitions();
  }, [loadTransitions]);

  const handleTransitionSelect = (transition: UnifiedTransition) => {
    setSelectedTransition(transition);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCreateClick = () => {
    setSelectedTransition(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedTransition(null);
  };

  const handleDelete = async (transitionId: string) => {
    try {
      const transitionToDelete = transitions.find((transition) => transition.id === transitionId);
      if (!transitionToDelete) {
        setError('Transition not found');
        return;
      }

      if (transitionToDelete.isPrimeTransition) {
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        await PrimeArtifactService.deletePrimeArtifactTransition(primeArtifactId, transitionId);
      } else {
        await TransitionService.deleteTransition(transitionId, artifactId);
      }
      setTransitions(transitions.filter((transition) => transition.id !== transitionId));
      if (selectedTransition?.id === transitionId) {
        setSelectedTransition(null);
      }
    } catch (err) {
      setError('Failed to delete transition');
      console.error('Error deleting transition:', err);
    }
  };

  const handleTransitionUpdate = async (transition: Partial<ArtifactItemTransition>) => {
    if (!transition.id) {
      setError('Cannot update transition without ID');
      return;
    }
    try {
      const existingTransition = transitions.find((t) => t.id === transition.id);
      if (!existingTransition) {
        setError('Transition not found');
        return;
      }

      let updatedTransition: ArtifactItemTransition;
      if (existingTransition.isPrimeTransition) {
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        const primeUpdate = {
          default_transition_type: transition.transition_type as any,
          transition_parameters: transition.transition_parameters,
          validation_rules: transition.validation_rules,
          demand_type: transition.demand_type as any,
          demand_flow_type: transition.demand_flow_type as any,
        };
        const primeTransition = await PrimeArtifactService.updatePrimeArtifactTransition(
          primeArtifactId,
          transition.id,
          primeUpdate
        );
        // Convert back to unified format
        updatedTransition = {
          id: primeTransition.id,
          name: primeTransition.name,
          description: primeTransition.description || undefined,
          artifact_ref: primeTransition.prime_artifact_ref,
          input_state_ref: primeTransition.from_state_ref,
          output_state_ref: primeTransition.to_state_ref,
          transition_type: primeTransition.default_transition_type,
          transition_parameters: primeTransition.transition_parameters,
          validation_rules: primeTransition.validation_rules,
          prime_artifact_state_transition_ref: primeTransition.id,
          demand_calculation_rules: {},
          improvement_impact: {},
          improvement_ref: undefined,
          artifact_process_ref: primeTransition.prime_process_ref,
          input_output_ratio: 1,
          effective_start_date: new Date().toISOString(),
          effective_end_date: null,
          is_active: true,
          created_at: primeTransition.created_at,
          updated_at: primeTransition.updated_at || new Date().toISOString(),
          isPrimeTransition: true,
        } as unknown as UnifiedTransition;
      } else {
        updatedTransition = await TransitionService.updateTransition(transition.id, transition);
        (updatedTransition as UnifiedTransition).isPrimeTransition = false;
      }

      setTransitions(
        transitions.map((t) =>
          t.id === transition.id ? (updatedTransition as UnifiedTransition) : t
        )
      );
      setSelectedTransition(updatedTransition as UnifiedTransition);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update transition');
      console.error('Error updating transition:', err);
    }
  };

  const handleTransitionCreate = async (transition: Partial<ArtifactItemTransition>) => {
    try {
      let newTransition: ArtifactItemTransition;
      if (usePrime) {
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        const primeCreate = {
          name: transition.name || '',
          description: transition.description || null,
          prime_artifact_ref: primeArtifactId,
          prime_process_ref: transition.artifact_process_ref || '',
          from_state_ref: transition.input_state_ref || '',
          to_state_ref: transition.output_state_ref || '',
          default_transition_type: transition.transition_type as any,
          is_secondary: transition.is_secondary || false,
          transition_parameters: transition.transition_parameters || {},
          validation_rules: transition.validation_rules || {},
          demand_type: transition.demand_type as any,
          demand_flow_type: transition.demand_flow_type as any,
          improvement_ref: transition.improvement_ref || null,
          improvement_parameters: transition.improvement_impact || {},
          is_improvement: transition.is_improvement ?? false,
        };
        const primeTransition = await PrimeArtifactService.createPrimeArtifactTransition(
          primeArtifactId,
          primeCreate
        );
        // Convert to unified format
        newTransition = {
          id: primeTransition.id,
          name: primeTransition.name,
          description: primeTransition.description || undefined,
          artifact_ref: primeTransition.prime_artifact_ref,
          input_state_ref: primeTransition.from_state_ref,
          output_state_ref: primeTransition.to_state_ref,
          transition_type: primeTransition.default_transition_type,
          transition_parameters: primeTransition.transition_parameters,
          validation_rules: primeTransition.validation_rules,
          prime_artifact_state_transition_ref: primeTransition.id,
          demand_calculation_rules: {},
          improvement_impact: {},
          improvement_ref: undefined,
          artifact_process_ref: primeTransition.prime_process_ref,
          input_output_ratio: 1,
          effective_start_date: new Date().toISOString(),
          effective_end_date: null,
          is_active: true,
          created_at: primeTransition.created_at,
          updated_at: primeTransition.updated_at || new Date().toISOString(),
          isPrimeTransition: true,
        } as unknown as UnifiedTransition;
      } else {
        newTransition = await TransitionService.createTransition({
          ...transition,
          artifact_ref: artifactId,
        });
        (newTransition as UnifiedTransition).isPrimeTransition = false;
      }
      setTransitions([...transitions, newTransition as UnifiedTransition]);
      setSelectedTransition(newTransition as UnifiedTransition);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to create transition');
      console.error('Error creating transition:', err);
    }
  };

  const handleCreateCompanyTransitionFromPrime = async (primeTransition: UnifiedTransition) => {
    try {
      if (!primeTransition.isPrimeTransition) {
        setError('Can only create company transitions from prime transitions');
        return;
      }

      // Get company states to map prime state refs to company state refs
      const companyStates = await StateService.getStatesByArtifact(artifactId);
      
      // Prime transitions use from_state_ref/to_state_ref (prime state IDs)
      // We need to find the corresponding company states that have prime_state_ref matching these
      const primeFromStateRef = (primeTransition as any).from_state_ref || primeTransition.input_state_ref;
      const primeToStateRef = (primeTransition as any).to_state_ref || primeTransition.output_state_ref;
      
      // Find company states that correspond to the prime states
      const inputCompanyState = companyStates.find(
        (state: any) => state.prime_state_ref === primeFromStateRef
      );
      const outputCompanyState = companyStates.find(
        (state: any) => state.prime_state_ref === primeToStateRef
      );

      if (!inputCompanyState) {
        setError(`Cannot create transition: Company state for prime state ${primeFromStateRef} not found. Please create the company state first.`);
        return;
      }

      if (!outputCompanyState) {
        setError(`Cannot create transition: Company state for prime state ${primeToStateRef} not found. Please create the company state first.`);
        return;
      }

      // Create a company transition based on the prime transition
      // Map prime transition's default_transition_type to company transition's transition_type
      // Prime transitions can be: PROCESSITEM, PROCESSITEM_PERSISTENT, MATERIAL, RESOURCE, CREATION
      // Company transitions can be: OPERATIONAL, MATERIAL, RESOURCE, CREATION, IMPROVEMENT
      let mappedTransitionType = 'OPERATIONAL'; // Default fallback
      const primeTransitionType = (primeTransition as any).default_transition_type || primeTransition.transition_type;
      
      if (primeTransitionType) {
        // Map prime transition types to company transition types
        const typeMapping: Record<string, string> = {
          'PROCESSITEM': 'OPERATIONAL',
          'PROCESSITEM_PERSISTENT': 'OPERATIONAL',
          'MATERIAL': 'MATERIAL',
          'RESOURCE': 'RESOURCE',
          'CREATION': 'CREATION',
        };
        mappedTransitionType = typeMapping[primeTransitionType] || 'OPERATIONAL';
      }
      
      // Set effective_end_date to a far future date if not provided (backend requires it)
      const effectiveEndDate = new Date();
      effectiveEndDate.setFullYear(effectiveEndDate.getFullYear() + 100); // 100 years from now
      
      const companyTransitionData: Partial<ArtifactItemTransition> & Record<string, any> = {
        name: primeTransition.name,
        description: primeTransition.description || undefined,
        input_state_ref: inputCompanyState.id, // Use company state ID
        output_state_ref: outputCompanyState.id, // Use company state ID
        transition_type: mappedTransitionType, // REQUIRED: Map from prime's default_transition_type
        transition_parameters: primeTransition.transition_parameters || {},
        validation_rules: primeTransition.validation_rules || {},
        prime_artifact_state_transition_ref: primeTransition.id, // Link to the prime transition
        demand_calculation_rules: {},
        improvement_impact: {},
        improvement_ref: undefined,
        artifact_process_ref: primeTransition.artifact_process_ref || (primeTransition as any).prime_process_ref,
        input_output_ratio: 1,
        effective_start_date: new Date().toISOString(),
        effective_end_date: effectiveEndDate.toISOString(), // REQUIRED: Set to far future date
        is_active: true,
      };

      console.log('🔄 [UnifiedTransitionManager] Creating company transition:', companyTransitionData);

      const newTransition = await TransitionService.createTransition({
        ...companyTransitionData,
        artifact_ref: artifactId,
      });
      const unifiedTransition = { ...newTransition, isPrimeTransition: false } as UnifiedTransition;

      setTransitions([...transitions, unifiedTransition]);
      setSelectedTransition(unifiedTransition);
      setError(null);
    } catch (err: any) {
      let errorMessage = 'Failed to create company transition from prime transition';
      
      // Provide more helpful error messages
      if (err?.response?.status === 400) {
        const errorDetail = err?.response?.data?.detail;
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (err?.message) {
          errorMessage = err.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error creating company transition from prime transition:', err);
    }
  };

  const handleApplyFromPrime = async () => {
    if (!artifact?.prime_artifact_id) {
      setError('This artifact is not linked to a prime artifact. Cannot apply from prime.');
      return;
    }

    const confirmed = window.confirm(
      'This will DELETE all existing transitions for this company artifact and replace them with transitions from the linked prime artifact.\n\nThis action cannot be undone.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(
        `/artifacts/${artifactId}/apply-prime-transitions`,
        null,
        { params: { prime_artifact_id: artifact.prime_artifact_id } }
      );
      
      // Refresh transitions after application
      await loadTransitions();
      
      alert(`Prime transitions applied successfully! Created ${response.data.transitions_created} transitions.`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to apply prime transitions';
      setError(errorMessage);
      console.error('Error applying prime transitions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter transitions based on improvement filter
  const filteredTransitions = transitions.filter((transition) => {
    if (improvementFilter === 'all') return true;
    if (improvementFilter === 'improvement') return transition.is_improvement === true;
    if (improvementFilter === 'operational') return transition.is_improvement === false;
    return true;
  });

  // Group transitions by name for compact display
  const groupedTransitions = filteredTransitions.reduce(
    (groups, transition) => {
      // Use transition name or fallback to ID if name is missing
      const name = transition.name || transition.id || 'Unnamed Transition';
      if (!groups[name]) {
        groups[name] = { prime: null, company: null };
      }
      if (transition.isPrimeTransition) {
        groups[name].prime = transition;
      } else {
        groups[name].company = transition;
      }
      return groups;
    },
    {} as Record<string, { prime: UnifiedTransition | null; company: UnifiedTransition | null }>
  );

  if (loading) {
    return <div>Loading transitions...</div>;
  }

  return (
    <div className={`${styles.transitionManager} ${styles.unifiedTransitionManager}`}>
      <div className={styles.transitionManagerHeader}>
        <h2>Transitions ({usePrime ? 'Creating Prime' : 'Creating Regular'})</h2>
        <div className={styles.transitionManagerActions}>
          <div className="filter-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label>Filter:</label>
            <select
              value={improvementFilter}
              onChange={(e) => setImprovementFilter(e.target.value as 'all' | 'operational' | 'improvement')}
              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
            >
              <option value="all">All</option>
              <option value="operational">Operational</option>
              <option value="improvement">Improvement</option>
            </select>
          </div>
          {artifact?.prime_artifact_id && (
            <button
              onClick={handleApplyFromPrime}
              className={styles.applyFromPrimeButton}
              disabled={loading}
              title="Apply all transitions from linked prime artifact"
            >
              Apply from Prime
            </button>
          )}
          <button onClick={handleCreateClick} className={styles.addTransitionButton}>
            Add Transition
          </button>
        </div>
      </div>

      <div className={styles.transitionManagerContent}>
        <div className={styles.transitionList}>
          {Object.keys(groupedTransitions).length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
              <p>No transitions found for this artifact.</p>
              <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                {artifact?.prime_artifact_id
                  ? 'Create a transition or apply transitions from the linked prime artifact.'
                  : 'Create a transition to get started.'}
              </p>
            </div>
          ) : (
            Object.entries(groupedTransitions).map(([name, group]) => {
            const primeTransition = group.prime;
            const companyTransition = group.company;
            const hasPrime = !!primeTransition;
            const hasCompany = !!companyTransition;

            return (
              <div
                key={name}
                className={`${styles.transitionGroup} ${selectedTransition?.name === name ? styles.selected : ''}`}
                onClick={() => {
                  const transitionToSelect = primeTransition || companyTransition;
                  if (transitionToSelect) {
                    handleTransitionSelect(transitionToSelect);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.transitionGroupHeader}>
                  <div className={styles.transitionNameSection}>
                    <span className={styles.transitionName} style={{ color: '#333', fontWeight: 500 }}>
                      {name || 'Unnamed Transition'}
                    </span>
                    <div className={styles.transitionActionsInline}>
                      {hasPrime && !hasCompany && (
                        <button
                          className={`${styles.iconButton} ${styles.createCompany}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCreateCompanyTransitionFromPrime(primeTransition!);
                          }}
                          title="Create Company Transition from Prime Transition"
                        >
                          +
                        </button>
                      )}
                      {hasCompany && (
                        <>
                          <button
                            className={`${styles.iconButton} ${styles.edit}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTransitionSelect(companyTransition!);
                              handleEditClick();
                            }}
                            title="Edit Company Transition"
                          >
                            ✏️
                          </button>
                          <button
                            className={`${styles.iconButton} ${styles.delete}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDelete(companyTransition!.id);
                            }}
                            title="Delete Company Transition"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.transitionBadges}>
                    {hasPrime && <span className={styles.primeBadge}>Prime</span>}
                    {hasCompany && <span className={styles.companyBadge}>Company</span>}
                    {(primeTransition?.is_improvement || companyTransition?.is_improvement) && (
                      <Tag color="purple" style={{ marginLeft: '4px' }}>Improvement</Tag>
                    )}
                  </div>
                </div>
                <div 
                  className={styles.transitionDetails}
                  style={{
                    backgroundColor: '#f0f8ff',
                    border: '2px solid #2196f3',
                    padding: '0.5rem',
                    margin: '0.25rem 0',
                    borderRadius: '4px',
                    display: 'block', // Override any flex display
                    width: '100%',
                  }}
                >
                  <span 
                    className={styles.transitionStates}
                    style={{
                      color: '#d32f2f',
                      backgroundColor: '#fff3cd',
                      padding: '0.5rem',
                      border: '2px solid #ff9800',
                      borderRadius: '4px',
                      display: 'inline-block', // Use inline-block instead of block
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      width: '100%',
                      minHeight: '1.5rem',
                      lineHeight: '1.4',
                      position: 'relative',
                      zIndex: 10,
                      visibility: 'visible',
                      opacity: 1,
                    }}
                  >
                    {(primeTransition || companyTransition) && (() => {
                      // Prefer prime transition for state names (they're more likely to be populated)
                      const transition = primeTransition || companyTransition!;
                      
                      // Debug logging - only sample in development to reduce verbosity
                      if (process.env.NODE_ENV === 'development' && Math.random() < 0.02) {
                        console.log('[UnifiedTransitionManager] Rendering transition (sampled):', {
                          transition_id: transition.id,
                          transition_name: transition.name,
                          from_state_name: transition.from_state_name,
                          to_state_name: transition.to_state_name
                        });
                      }
                      
                      // Prefer state names from prime transition if available, otherwise use company transition
                      const fromStateName = primeTransition?.from_state_name || companyTransition?.from_state_name;
                      const toStateName = primeTransition?.to_state_name || companyTransition?.to_state_name;
                      
                      // Prefer state names, but show refs if names aren't available (truncated for readability)
                      const fromState = fromStateName || 
                        (transition.input_state_ref ? String(transition.input_state_ref).substring(0, 8) + '...' : '?');
                      const toState = toStateName || 
                        (transition.output_state_ref ? String(transition.output_state_ref).substring(0, 8) + '...' : '?');
                      
                      // If we have no state info at all, show a message
                      const hasNoStateInfo = !fromStateName && !toStateName && 
                                             !transition.input_state_ref && !transition.output_state_ref;
                      
                      if (hasNoStateInfo) {
                        return (
                          <span className={styles.noStateInfo}>
                            No state information available
                          </span>
                        );
                      }
                      
                      return (
                        <span style={{ display: 'inline-block', width: '100%' }}>
                          <span 
                            className={fromStateName ? styles.stateNameResolved : styles.stateNameUnresolved}
                            style={{
                              color: fromStateName ? '#d32f2f' : '#999',
                              fontWeight: 600,
                              backgroundColor: '#fff3cd',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '2px',
                              display: 'inline-block',
                              marginRight: '0.5rem',
                              minWidth: '60px',
                              textAlign: 'center',
                            }}
                          >
                            {fromState}
                          </span>
                          <span style={{ 
                            color: '#d32f2f', 
                            fontWeight: 700, 
                            margin: '0 0.5rem',
                            fontSize: '1rem',
                            display: 'inline-block',
                          }}>
                            {'→'}
                          </span>
                          <span 
                            className={toStateName ? styles.stateNameResolved : styles.stateNameUnresolved}
                            style={{
                              color: toStateName ? '#d32f2f' : '#999',
                              fontWeight: 600,
                              backgroundColor: '#fff3cd',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '2px',
                              display: 'inline-block',
                              minWidth: '60px',
                              textAlign: 'center',
                            }}
                          >
                            {toState}
                          </span>
                        </span>
                      );
                    })()}
                  </span>
                </div>
              </div>
            );
          })
          )}
        </div>

        <div className={styles.transitionDetailsPanel}>
          {isEditing ? (
            <UnifiedTransitionForm
              onSubmit={selectedTransition ? handleTransitionUpdate : handleTransitionCreate}
              onCancel={handleCancel}
              initialTransition={selectedTransition as any}
              usePrime={usePrime}
              artifactId={
                usePrime
                  ? (artifact?.prime_artifact_id as unknown as CompanyArtifactId) || artifactId
                  : artifactId
              }
              artifact={artifact}
            />
          ) : selectedTransition ? (
            <div className={styles.transitionView}>
              <h3>{selectedTransition.name}</h3>
              <p>{selectedTransition.description}</p>
              <div className={styles.transitionMetadata}>
                <p>
                  <strong>From:</strong>{' '}
                  {selectedTransition.from_state_name || selectedTransition.input_state_ref}
                </p>
                <p>
                  <strong>To:</strong>{' '}
                  {selectedTransition.to_state_name || selectedTransition.output_state_ref}
                </p>
                <p>
                  <strong>Type:</strong>{' '}
                  {selectedTransition.default_transition_type || selectedTransition.transition_type}
                </p>
                <p>
                  <strong>Input/Output Ratio:</strong> {selectedTransition.input_output_ratio}
                </p>
                <p>
                  <strong>Demand Type:</strong> {selectedTransition.demand_type}
                </p>
                <p>
                  <strong>Flow Type:</strong> {selectedTransition.demand_flow_type}
                </p>
                <p>
                  <strong>Active:</strong> {selectedTransition.is_active ? 'Yes' : 'No'}
                </p>
              </div>
              <button onClick={handleEditClick} className={styles.editButton}>
                Edit
              </button>
            </div>
          ) : (
            <div className={styles.noSelection} style={{ padding: '2rem', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>
              <p>Select a transition from the list to view details.</p>
              <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Click on any transition name above to see its details here.</p>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
};
